import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassSelect, ModuleHeader, EmailChipInput } from '../ui';
import { getProjects, getBrandGuides, createCampaign, generateCampaign, getCampaignGenerationProgress, Project, AD_IMAGE_TYPES } from '../../services/api';
import CampaignGenerationProgressModal from './CampaignGenerationProgressModal';
import './Campaigns.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const ASSET_TYPES = [
    { key: 'landing_page', label: 'Landing Page' },
    { key: 'blog_post', label: 'Blog Post' },
    { key: 'social_linkedin', label: 'LinkedIn Post' },
    { key: 'social_twitter', label: 'Twitter / X' },
    { key: 'social_instagram', label: 'Instagram' },
    { key: 'social_facebook', label: 'Facebook' },
    { key: 'ad_copy', label: 'Ad Copy' },
];

const STEPS = ['Brief', 'Brand', 'Generate'];

const GOAL_OPTIONS = [
    { value: 'awareness', label: 'Brand Awareness' },
    { value: 'lead_gen', label: 'Lead Generation' },
    { value: 'promotion', label: 'Promotion / Sales' },
    { value: 'retention', label: 'Customer Retention' },
];

const CampaignCreator: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [projects, setProjects] = useState<Project[]>([]);
    const [brandGuides, setBrandGuides] = useState<any[]>([]);
    const [generating, setGenerating] = useState(false);
    // Captured between createCampaign() and generateCampaign() so the
    // progress modal has an id to poll while the long-running generate POST
    // is in flight.
    const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);
    const [error, setError] = useState('');

    // Form state
    const [projectId, setProjectId] = useState('');
    const [name, setName] = useState('');
    const [productService, setProductService] = useState('');
    const [goal, setGoal] = useState('awareness');
    const [audience, setAudience] = useState('');
    const [offer, setOffer] = useState('');
    const [audienceSegments, setAudienceSegments] = useState<string[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [description, setDescription] = useState('');

    const [selectedBrandGuide, setSelectedBrandGuide] = useState('');
    const [selectedAssets, setSelectedAssets] = useState<string[]>(ASSET_TYPES.map(a => a.key));
    const [variantCount, setVariantCount] = useState(1);
    const [adAutoImages, setAdAutoImages] = useState<boolean>(false);   // DEFAULT OFF
    const [adImageType, setAdImageType] = useState<string>('photo');

    useEffect(() => {
        getProjects().then(setProjects).catch(console.error);
    }, []);

    useEffect(() => {
        if (!projectId) { setBrandGuides([]); setSelectedBrandGuide(''); return; }
        getBrandGuides(projectId).then((guides) => {
            setBrandGuides(guides);
            // Auto-select: prefer is_default, then first active, then first available
            const defaultBg = guides.find((bg: any) => bg.is_default === 1 && bg.status === 'active');
            const activeBg = guides.find((bg: any) => bg.status === 'active');
            const best = defaultBg || activeBg || guides[0];
            if (best && !selectedBrandGuide) {
                setSelectedBrandGuide(best.brand_guide_id);
            }
        }).catch(console.error);
    }, [projectId]);

    const toggleAsset = (key: string) => {
        setSelectedAssets(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const canProceedStep0 = !!(projectId && name.trim() && productService.trim());

    const handleGenerate = async () => {
        if (!canProceedStep0) return;
        setError('');
        setGenerating(true);
        try {
            const campaign = await createCampaign({
                project_id: projectId,
                name: name.trim(),
                description: description.trim() || undefined,
                product_service: productService.trim(),
                campaign_goal: goal,
                target_audience: audience.trim() || undefined,
                offer: offer.trim() || undefined,
                audience_segments: audienceSegments.length ? audienceSegments : undefined,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            });
            setCreatedCampaignId(campaign.campaign_id);

            // POST /generate returns 202 immediately; the work runs in
            // aime-scheduler. The modal owns terminal-state detection +
            // navigation (via onComplete) and Retry / Back-to-wizard
            // (via onRetry / onClose), so this handler just kicks off the
            // request and bows out — no parallel polling loop here.
            try {
                await generateCampaign(
                    campaign.campaign_id,
                    selectedAssets,
                    selectedBrandGuide || undefined,
                    variantCount,
                    undefined,
                    undefined,
                    adAutoImages,
                    adImageType,
                );
            } catch (e) {
                // 504 on the enqueue itself is uncommon (handler is fast)
                // but possible under load — modal's poller will pick up the
                // real status either way.
                setError(getApiError(e, ''));
            }
        } catch (err: any) {
            setError(getApiError(err, 'Generation failed. Please try again.'));
            setGenerating(false);
        }
    };

    // Build project options for GlassSelect
    const projectOptions = [
        { value: '', label: 'Select project…' },
        ...projects.map(p => ({ value: p.project_id, label: p.name })),
    ];

    const brandGuideOptions = [
        { value: '', label: 'No brand guide (skip)' },
        ...brandGuides.map((bg: any) => ({
            value: bg.brand_guide_id,
            label: bg.brand_guide_name || bg.brand_name || 'Unnamed',
        })),
    ];

    const selectedBrandGuideName = brandGuides.find(
        (b: any) => b.brand_guide_id === selectedBrandGuide
    )?.brand_guide_name || 'Selected';

    if (generating) {
        return (
            <div className="campaign-creator">
                {createdCampaignId ? (
                    <CampaignGenerationProgressModal
                        campaignId={createdCampaignId}
                        isOpen={true}
                        onRetry={async () => {
                            try {
                                // Resume mode preserves successful assets +
                                // narrative from the prior attempt; the
                                // generator only fires missing/failed slots.
                                await generateCampaign(
                                    createdCampaignId,
                                    selectedAssets,
                                    selectedBrandGuide || undefined,
                                    variantCount,
                                    undefined,
                                    /* resume */ true,
                                    adAutoImages,
                                    adImageType,
                                );
                            } catch { /* modal's own poller picks up new status */ }
                        }}
                        onClose={() => setGenerating(false)}
                        onComplete={() => navigate(`/campaigns/${createdCampaignId}`)}
                    />
                ) : (
                    <GlassCard className="generating-overlay">
                        <div className="generating-spinner" />
                        <p style={{ fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                            Creating campaign…
                        </p>
                    </GlassCard>
                )}
            </div>
        );
    }

    return (
        <div className="campaign-creator">
            <ModuleHeader title="New Campaign" />

            {/* Step indicators */}
            <div className="campaign-creator-steps">
                {STEPS.map((label, i) => (
                    <div
                        key={label}
                        className={`creator-step-indicator${i === step ? ' active' : i < step ? ' completed' : ''}`}
                    >
                        <div className="creator-step-dot">
                            {i < step ? <Check size={14} /> : i + 1}
                        </div>
                        <span className="creator-step-label">{label}</span>
                    </div>
                ))}
            </div>

            <GlassCard className="campaign-creator-body">
                {step === 0 && (
                    <>
                        <h3>Campaign Brief</h3>
                        <div className="form-group">
                            <label>Project *</label>
                            <GlassSelect
                                value={projectId}
                                onChange={setProjectId}
                                options={projectOptions}
                            />
                        </div>
                        <div className="form-group">
                            <label>Campaign Name *</label>
                            <GlassInput
                                placeholder="e.g. Q2 Product Launch"
                                value={name}
                                onChange={setName}
                            />
                        </div>
                        <div className="form-group">
                            <label>Product / Service *</label>
                            <GlassInput
                                placeholder="What are you promoting?"
                                value={productService}
                                onChange={setProductService}
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Campaign Goal</label>
                                <GlassSelect
                                    value={goal}
                                    onChange={setGoal}
                                    options={GOAL_OPTIONS}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Target Audience</label>
                            <GlassInput
                                placeholder="e.g. Small business owners in Australia aged 30-50"
                                value={audience}
                                onChange={setAudience}
                            />
                        </div>
                        <div className="form-group">
                            <label>Offer</label>
                            <GlassInput
                                placeholder="e.g. Fixed-fee will review, Free 15-min consult"
                                value={offer}
                                onChange={setOffer}
                            />
                        </div>
                        <div className="form-group">
                            <EmailChipInput
                                label="Audience Segments (optional)"
                                placeholder="e.g. blended families — press Enter to add"
                                value={audienceSegments}
                                onChange={setAudienceSegments}
                                validator={(s) => s.trim().length > 0}
                                errorMessage="Not a valid entry"
                                helperText="Leave empty to let AI infer segments at generation time."
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Start Date</label>
                                <GlassInput
                                    type="date"
                                    value={startDate}
                                    onChange={setStartDate}
                                />
                            </div>
                            <div className="form-group">
                                <label>End Date</label>
                                <GlassInput
                                    type="date"
                                    value={endDate}
                                    onChange={setEndDate}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Additional Notes</label>
                            <GlassInput
                                placeholder="Any extra context for the AI…"
                                value={description}
                                onChange={setDescription}
                            />
                        </div>
                    </>
                )}

                {step === 1 && (
                    <>
                        <h3>Brand Guide (optional)</h3>
                        <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                            Select a brand guide to give the AI context about your brand voice, colours, and tone.
                        </p>
                        <div className="form-group">
                            <label>Brand Guide</label>
                            <GlassSelect
                                value={selectedBrandGuide}
                                onChange={setSelectedBrandGuide}
                                options={brandGuideOptions}
                            />
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h3>Review &amp; Generate</h3>
                        <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                            Select which assets to generate. Gemini will create all selected assets from your brief.
                        </p>
                        <div className="asset-checklist">
                            {ASSET_TYPES.map(a => (
                                <label
                                    key={a.key}
                                    className={`asset-check-item${selectedAssets.includes(a.key) ? ' selected' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedAssets.includes(a.key)}
                                        onChange={() => toggleAsset(a.key)}
                                    />
                                    {a.label}
                                </label>
                            ))}
                        </div>

                        <div className="form-group" style={{ marginTop: 12 }}>
                            <label>Variant Count</label>
                            <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                Generate multiple distinct variations per asset for A/B testing.
                                Each variant uses a different persuasion angle.
                            </p>
                            <div className="variant-count-selector">
                                {[1, 2, 3, 4, 5].map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`variant-count-btn${variantCount === n ? ' active' : ''}`}
                                        onClick={() => setVariantCount(n)}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {selectedAssets.includes('ad_copy') && (
                            <div className="form-group" style={{ marginTop: 12 }}>
                                <label className={`asset-check-item${adAutoImages ? ' selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={adAutoImages}
                                        onChange={() => setAdAutoImages(v => !v)}
                                    />
                                    Generate ad images
                                </label>
                                {adAutoImages && (
                                    <div style={{ marginTop: 8 }}>
                                        <label>Ad Image Style</label>
                                        <GlassSelect
                                            options={AD_IMAGE_TYPES.map(t => ({ value: t.id, label: t.label }))}
                                            value={adImageType}
                                            onChange={setAdImageType}
                                        />
                                    </div>
                                )}
                                <p style={{ margin: '8px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                    Generates one image per platform (Google, Meta, LinkedIn) for the first variant only —
                                    up to 3 images per campaign. Generate the rest individually from Ad Copy → Saved Ads.
                                </p>
                            </div>
                        )}

                        <div style={{ padding: '12px', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', fontSize: '0.85rem' }}>
                            <strong>Campaign:</strong> {name}<br />
                            <strong>Product:</strong> {productService}<br />
                            <strong>Goal:</strong> {goal}<br />
                            {selectedBrandGuide && <><strong>Brand Guide:</strong> {selectedBrandGuideName}<br /></>}
                            <strong>Assets to generate:</strong> {selectedAssets.length}
                            {variantCount > 1 && <><br /><strong>Variants per asset:</strong> {variantCount}</>}
                        </div>

                        {error && (
                            <p style={{ color: 'var(--danger, #dc2626)', margin: 0, fontSize: '0.85rem' }}>{error}</p>
                        )}
                    </>
                )}
            </GlassCard>

            <div className="creator-nav">
                <GlassButton
                    variant="secondary"
                    onClick={() => step === 0 ? navigate('/campaigns') : setStep(s => s - 1)}
                >
                    <ChevronLeft size={16} />
                    {step === 0 ? 'Cancel' : 'Back'}
                </GlassButton>

                {step < 2 ? (
                    <GlassButton
                        variant="primary"
                        onClick={() => setStep(s => s + 1)}
                        disabled={step === 0 && !canProceedStep0}
                    >
                        Next
                        <ChevronRight size={16} />
                    </GlassButton>
                ) : (
                    <GlassButton
                        variant="primary"
                        onClick={handleGenerate}
                        disabled={selectedAssets.length === 0}
                    >
                        <Zap size={16} />
                        Generate Bundle
                    </GlassButton>
                )}
            </div>
        </div>
    );
};

export default CampaignCreator;
